import { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/hooks/useAuth';
import Shell from '@/components/Shell';
import Splash from '@/pages/Splash';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Onboarding from '@/pages/Onboarding';
import Discover from '@/pages/Discover';
import EventDetail from '@/pages/EventDetail';
import Explorer from '@/pages/Explorer';
import Agenda from '@/pages/Agenda';
import Carnet from '@/pages/Carnet';
import Vacation from '@/pages/Vacation';
import Alerts from '@/pages/Alerts';
import Legal from '@/pages/Legal';
import LegalPublic from '@/pages/LegalPublic';
import Search from '@/pages/Search';
import AdminCoverage from '@/pages/AdminCoverage';
import { useStartupNotifications } from '@/hooks/useStartupNotifications';
import { registerAuthDeepLink } from '@/lib/authDeepLink';

/** Redirects logged-in users who haven't finished onboarding. */
function OnboardingRedirect() {
  const { user, profile, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (loading) return;
    const exempt = ['/onboarding', '/login', '/auth/callback'];
    if (user && profile && profile.onboarded_at == null && !exempt.includes(loc.pathname)) {
      nav('/onboarding', { replace: true });
    }
  }, [loading, user, profile, loc.pathname, nav]);
  return null;
}

/** Auth gate — redirects unauthenticated users to /login with returnTo. */
function Gate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (!user) nav('/login', { replace: true, state: { returnTo: loc.pathname } });
  }, [user, loc.pathname, nav]);
  if (!user) return null;
  return <Outlet />;
}

export default function App() {
  const { loading, user, profile } = useAuth();
  const nav = useNavigate();
  const [bootDelayDone, setBootDelayDone] = useState(false);

  useStartupNotifications(Boolean(profile?.notifications_opt_in), user?.id);

  useEffect(() => {
    registerAuthDeepLink((next) => nav(next, { replace: true }));
  }, [nav]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setBootDelayDone(true), 1100);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const showLaunchScreen = useMemo(() => loading || !bootDelayDone, [bootDelayDone, loading]);

  if (showLaunchScreen) return <Splash />;

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route element={<><OnboardingRedirect /><Shell /></>}>
          <Route index element={<Navigate to="/decouvrir" replace />} />
          {/* Public routes — accessible without account */}
          <Route path="/decouvrir" element={<Discover />} />
          <Route path="/explorer" element={<Explorer />} />
          <Route path="/recherche" element={<Search />} />
          <Route path="/vacation" element={<Vacation />} />
          <Route path="/mentions" element={<LegalPublic />} />
          {/* Agenda (favoris) — accessible sans compte (favoris locaux en invité) */}
          <Route path="/agenda" element={<Agenda />} />
          {/* Private routes — require auth */}
          <Route element={<Gate />}>
            <Route path="/carnet" element={<Carnet />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/admin/coverage" element={<AdminCoverage />} />
          </Route>
        </Route>
        {/* Event detail — public */}
        <Route path="/event/:id" element={<EventDetail />} />
        <Route path="*" element={<Navigate to="/decouvrir" replace />} />
      </Routes>
      {!Capacitor.isNativePlatform() && (
        <span
          className="fixed right-2 z-50 font-mono text-[10px] text-black/60 select-none pointer-events-none"
          style={{ bottom: 'calc(var(--tabbar-h) + env(safe-area-inset-bottom) + 6px)' }}
        >
          {__COMMIT__}
        </span>
      )}
    </>
  );
}
